/**
 * 语文听写 —— **批量任务**的接口层（迁移 0020，2026-10-28）。
 *
 * ## 用户要的是什么
 *
 * > "我可以一次性布置一本书或者几本书的听写任务，他可以分次去完成。"
 * > "孩子不能结束整个任务，家长可以结束、暂停或删除整个任务。"
 *
 * 一个任务 = "范围 + 每次几个 + 顺序 + 节奏 + 状态"，**不含词**：
 * 词是孩子每次点「开始听写」时由服务端现取的（`startNextBatch`）。
 *
 * | 方法 | 路径 | 谁 |
 * | --- | --- | --- |
 * | GET   | `/students/:id/dictation/assignments` | 孩子 + 家长 |
 * | POST  | `/students/:id/dictation/assignments` | 只有家长 |
 * | GET   | `/students/:id/dictation/assignments/:aid` | 孩子 + 家长 |
 * | PATCH | `/students/:id/dictation/assignments/:aid` | 只有家长（暂停/恢复/结束） |
 * | DELETE| `/students/:id/dictation/assignments/:aid` | 只有家长 |
 * | POST  | `/students/:id/dictation/assignments/:aid/next-batch` | 孩子 + 家长 |
 * | POST  | `/students/:id/dictation/sessions/:sid/submit` | 孩子 + 家长 |
 *
 * ## 解码口径：与听写其它接口一致 —— **显式解码，绝不静默兜底**
 *
 * 三态必须分得开（本项目反复踩过的线）：
 *   · 数字 `0` —— 真的是 0；
 *   · `null` —— **字段没拿到**（老后端 / 形状变了）；
 *   · 数组 `[]` vs `null` —— 空列表 vs 列表没拿到。
 *
 * 进度里的 `totalWords` / `remainingWords` 尤其不能猜：界面上"还剩 0 个词"
 * 与"拿不到进度"是两句完全不同的话（前者该显示"听写完成 🎉"）。
 */
import { DEFAULT_DICTATION_TEMPO, type DictationTempoConfig } from '@fht/shared';
import { api } from './client';
import { decodeSessionDetail, type DictationSessionDetailView } from './dictationSessions';

// ---------------------------------------------------------------- 工具

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toStringOrEmpty(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function toOptionalString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function toOptionalNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
}

// ---------------------------------------------------------------- 视图类型

/** 任务状态（与后端 `DICTATION_ASSIGNMENT_STATUSES` 一致）。 */
export type AssignmentStatusView = 'active' | 'paused' | 'finished';

/** 任务范围里的一本书。 */
export interface AssignmentBookView {
  id: string;
  name: string;
  publisher: string;
  gradeCode: string;
  semester: string;
}

/** 任务进度。每个数字都是后端当场算的（前端不重算）。 */
export interface AssignmentProgressView {
  /** 范围里一共有多少个词；`null` = **没拿到**（不是 0）。 */
  totalWords: number | null;
  /** 已提交的 session 里出现过的词数（去重）。 */
  listenedWords: number | null;
  /** 还剩多少个词（`null` = 没拿到）。 */
  remainingWords: number | null;
  /** 被判成「写错 / 没写出来」的不同词数。 */
  wrongWords: number | null;
  /** 已提交的批数。 */
  submittedBatches: number | null;
  lastBatchAt: string | null;
  /** 词全部听完了（后端算的；`null` = 没拿到，**不要自己推**）。 */
  allDone: boolean | null;
}

export interface AssignmentView {
  id: string;
  studentId: string;
  createdByParentId: string;
  title: string;
  /** 任务挂的书 id（可能是空数组 = 按年级 + 学期）。 */
  textbookIds: string[];
  /** 书的**内容**（书被删了就少一条）。`null` = 这个字段没拿到。 */
  books: AssignmentBookView[] | null;
  gradeCode: string | null;
  semester: string | null;
  category: string | null;
  batchSize: number;
  /** `false` = 按课本顺序；`true` = 随机。 */
  shuffle: boolean;
  tempo: DictationTempoConfig;
  status: AssignmentStatusView;
  progress: AssignmentProgressView;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------- 解码

function decodeTempo(raw: unknown): DictationTempoConfig {
  const r = isRecord(raw) ? raw : {};
  const num = (key: keyof DictationTempoConfig, fallback: number): number => {
    const value = toOptionalNumber(r[key]);
    return value === null ? fallback : value;
  };
  return {
    repeatCount: num('repeatCount', DEFAULT_DICTATION_TEMPO.repeatCount),
    gapSeconds: num('gapSeconds', DEFAULT_DICTATION_TEMPO.gapSeconds),
    betweenWordsSeconds: num('betweenWordsSeconds', DEFAULT_DICTATION_TEMPO.betweenWordsSeconds),
  };
}

/** 状态解码：认不出来的**当成 `active`**（保守：宁可让孩子能点，也不要让任务卡死）。 */
export function decodeAssignmentStatus(raw: unknown): AssignmentStatusView {
  return raw === 'paused' || raw === 'finished' ? raw : 'active';
}

function decodeProgress(raw: unknown): AssignmentProgressView {
  const r = isRecord(raw) ? raw : {};
  return {
    totalWords: toOptionalNumber(r['totalWords']),
    listenedWords: toOptionalNumber(r['listenedWords']),
    remainingWords: toOptionalNumber(r['remainingWords']),
    wrongWords: toOptionalNumber(r['wrongWords']),
    submittedBatches: toOptionalNumber(r['submittedBatches']),
    lastBatchAt: toOptionalString(r['lastBatchAt']),
    allDone: typeof r['allDone'] === 'boolean' ? r['allDone'] : null,
  };
}

function decodeBook(raw: Record<string, unknown>): AssignmentBookView {
  return {
    id: toStringOrEmpty(raw['id']),
    name: toStringOrEmpty(raw['name']),
    publisher: toStringOrEmpty(raw['publisher']),
    gradeCode: toStringOrEmpty(raw['gradeCode']),
    semester: toStringOrEmpty(raw['semester']),
  };
}

export function decodeAssignment(raw: unknown): AssignmentView {
  const r = isRecord(raw) ? raw : {};
  const books = Array.isArray(r['books'])
    ? r['books'].map((item) => decodeBook(isRecord(item) ? item : {}))
    : null;
  return {
    id: toStringOrEmpty(r['id']),
    studentId: toStringOrEmpty(r['studentId']),
    createdByParentId: toStringOrEmpty(r['createdByParentId']),
    title: toStringOrEmpty(r['title']),
    textbookIds: toStringList(r['textbookIds']),
    books,
    gradeCode: toOptionalString(r['gradeCode']),
    semester: toOptionalString(r['semester']),
    category: toOptionalString(r['category']),
    batchSize: toOptionalNumber(r['batchSize']) ?? 10,
    shuffle: r['shuffle'] === true,
    tempo: decodeTempo(r['tempo']),
    status: decodeAssignmentStatus(r['status']),
    progress: decodeProgress(r['progress']),
    createdAt: toStringOrEmpty(r['createdAt']),
    updatedAt: toStringOrEmpty(r['updatedAt']),
  };
}

// ---------------------------------------------------------------- 端点：任务

/** 任务列表。`assignments === null` = **这份数据没拿到**（不是"没有任务"）。 */
export async function listAssignments(
  studentId: string,
  query: { status?: AssignmentStatusView; limit?: number; offset?: number } = {},
): Promise<{ assignments: AssignmentView[] | null; total: number | null; missing: boolean }> {
  const data = await api.get<unknown>(`/students/${studentId}/dictation/assignments`, {
    ...(query.status !== undefined ? { status: query.status } : {}),
    limit: query.limit ?? 50,
    offset: query.offset ?? 0,
  });
  if (!isRecord(data) || !Array.isArray(data['assignments'])) {
    return {
      assignments: null,
      total: isRecord(data) ? toOptionalNumber(data['total']) : null,
      missing: true,
    };
  }
  return {
    assignments: (data['assignments'] as unknown[]).map((item) => decodeAssignment(item)),
    total: toOptionalNumber(data['total']),
    missing: false,
  };
}

export async function getAssignment(
  studentId: string,
  assignmentId: string,
): Promise<AssignmentView> {
  const data = await api.get<unknown>(
    `/students/${studentId}/dictation/assignments/${assignmentId}`,
  );
  return decodeAssignment(data);
}

export interface CreateAssignmentPayload {
  title?: string;
  /** 范围：几本书（可空，空了就用 `gradeCode` + `semester`）。 */
  textbookIds?: string[];
  gradeCode?: string;
  semester?: string;
  category?: string;
  batchSize: number;
  shuffle: boolean;
  tempo?: DictationTempoConfig;
}

export async function createAssignment(
  studentId: string,
  payload: CreateAssignmentPayload,
): Promise<AssignmentView> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/assignments`,
    payload,
  );
  return decodeAssignment(data);
}

export interface UpdateAssignmentPayload {
  title?: string;
  batchSize?: number;
  shuffle?: boolean;
  tempo?: DictationTempoConfig;
  status?: AssignmentStatusView;
}

export async function updateAssignment(
  studentId: string,
  assignmentId: string,
  payload: UpdateAssignmentPayload,
): Promise<AssignmentView> {
  const data = await api.patch<unknown>(
    `/students/${studentId}/dictation/assignments/${assignmentId}`,
    payload,
  );
  return decodeAssignment(data);
}

/**
 * 删任务。
 *
 * 已**提交**的批不会被删（后端外键是 `SET NULL`，它们变成"独立听写"）；
 * **还没提交**的批会随之一起清掉 —— `discardedBatches` 就是清掉的批数，
 * 界面要如实说出来（"顺手清掉了 N 批还没交的"）。
 */
export async function deleteAssignment(
  studentId: string,
  assignmentId: string,
): Promise<{ deleted: boolean; discardedBatches: number }> {
  const data = await api.del<unknown>(
    `/students/${studentId}/dictation/assignments/${assignmentId}`,
  );
  const record = isRecord(data) ? data : {};
  return {
    deleted: record['deleted'] === true,
    discardedBatches: toOptionalNumber(record['discardedBatches']) ?? 0,
  };
}

/** 「开始听写」的结果：这一批的会话（含题目）+ 刷新后的任务进度。 */
export interface NextBatchResultView {
  session: DictationSessionDetailView;
  assignment: AssignmentView | null;
  batchNo: number | null;
}

/**
 * 取下一批（孩子点「开始听写」）。
 *
 * ⚠️ 三种"拿不到新的一批"都会**抛**（不是返回空会话），调用方要按错误码分流：
 *   · `DICTATION_ASSIGNMENT_DONE`（409）—— 全部听完了（界面上是"完成 🎉"）；
 *   · `DICTATION_ASSIGNMENT_INACTIVE`（409）—— 家长暂停/结束了；
 *   · `DICTATION_ASSIGNMENT_NOT_FOUND`（404）—— 任务被删了。
 */
export async function startNextBatch(
  studentId: string,
  assignmentId: string,
): Promise<NextBatchResultView> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/assignments/${assignmentId}/next-batch`,
    {},
  );
  const r = isRecord(data) ? data : {};
  return {
    session: decodeSessionDetail(r['session']),
    assignment: isRecord(r['assignment']) ? decodeAssignment(r['assignment']) : null,
    batchNo: toOptionalNumber(r['batchNo']),
  };
}

/**
 * **提交这一批**（孩子点「提交」）。
 *
 * 提交之后这一批的词才算"听过"（任务进度靠它推进）。已批改的会话不会被退回。
 */
export async function submitBatch(
  studentId: string,
  sessionId: string,
): Promise<DictationSessionDetailView> {
  const data = await api.post<unknown>(
    `/students/${studentId}/dictation/sessions/${sessionId}/submit`,
    {},
  );
  return decodeSessionDetail(data);
}

// ---------------------------------------------------------------- 纯展示辅助（可测）

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatusView, string> = {
  active: '进行中',
  paused: '已暂停',
  finished: '已结束',
};

export function assignmentStatusLabel(status: AssignmentStatusView): string {
  return ASSIGNMENT_STATUS_LABEL[status];
}

/**
 * 进度的一句话（"已听 30 / 215 个 · 还剩 185 个"）。
 *
 * ⚠️ 三个数字**任一拿不到**就整句退化成"进度没拿到" —— 不能把 `null` 当 0
 *    （"还剩 0 个"会让孩子以为做完了，而真相是接口没给）。
 */
export function describeProgress(progress: AssignmentProgressView): string {
  const { listenedWords, totalWords, remainingWords } = progress;
  if (listenedWords === null || totalWords === null || remainingWords === null) {
    return '进度没拿到（刷新一下试试）';
  }
  if (totalWords === 0) return '这个范围里一个词都没有';
  return `已听 ${listenedWords} / ${totalWords} 个 · 还剩 ${remainingWords} 个`;
}

/** 进度条百分比（拿不到时 0；0 个词时 0）。 */
export function progressPercent(progress: AssignmentProgressView): number {
  const { listenedWords, totalWords } = progress;
  if (listenedWords === null || totalWords === null || totalWords <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((listenedWords / totalWords) * 100)));
}

/** 顺序的人话。 */
export function describeOrder(shuffle: boolean): string {
  return shuffle ? '每次随机取' : '按课本顺序';
}
